import { Tooltip } from "antd";
import { useCallback } from "react";
import { color } from "theme/aiInventory";

export type CompositionSegment = {
  /** Stable identifier used when the bar is clickable. */
  key: string;
  color: string;
  label: string;
  value: number;
};

export type CompositionBarProps = {
  height?: number;
  segments: CompositionSegment[];
  /** Highlight this segment (filter is active). */
  activeKey?: string;
  /** If provided, segments become clickable and toggle the filter. */
  onSelect?: (key: string | undefined) => void;
};

type SegmentProps = {
  segment: CompositionSegment;
  percent: number;
  active: boolean;
  interactive: boolean;
  onClick?: (e: React.MouseEvent) => void;
};

const Segment: React.FC<SegmentProps> = ({
  segment,
  percent,
  active,
  interactive,
  onClick,
}) => (
  <Tooltip title={`${segment.label}: ${segment.value}`}>
    <button
      type="button"
      className="p0-bar-seg"
      disabled={!interactive}
      onClick={onClick}
      aria-label={`${segment.label}: ${segment.value}`}
      style={{
        width: `${percent}%`,
        height: "100%",
        background: segment.color,
        border: "none",
        padding: 0,
        cursor: interactive ? "pointer" : "default",
        opacity: active ? 1 : interactive ? 0.7 : 1,
        transition: "opacity 120ms ease",
      }}
    />
  </Tooltip>
);

type InteractiveSegmentProps = {
  segment: CompositionSegment;
  percent: number;
  active: boolean;
  activeKey: string | undefined;
  onSelect: (key: string | undefined) => void;
};

const InteractiveSegment: React.FC<InteractiveSegmentProps> = ({
  segment,
  percent,
  active,
  activeKey,
  onSelect,
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(activeKey === segment.key ? undefined : segment.key);
    },
    [activeKey, onSelect, segment.key]
  );
  return (
    <Segment
      segment={segment}
      percent={percent}
      active={active}
      interactive
      onClick={handleClick}
    />
  );
};

export const CompositionBar: React.FC<CompositionBarProps> = ({
  segments,
  height = 6,
  activeKey,
  onSelect,
}) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const barStyle: React.CSSProperties = {
    width: "100%",
    height,
    borderRadius: height / 2,
    display: "flex",
    overflow: "hidden",
    background: color.mute,
  };
  if (total === 0) {
    return <div style={barStyle} />;
  }
  return (
    <div role="img" aria-label="breakdown" style={barStyle}>
      {segments.map((s) => {
        if (s.value === 0) return null;
        const percent = (s.value / total) * 100;
        const active = activeKey === s.key;
        return onSelect ? (
          <InteractiveSegment
            key={s.key}
            segment={s}
            percent={percent}
            active={active}
            activeKey={activeKey}
            onSelect={onSelect}
          />
        ) : (
          <Segment
            key={s.key}
            segment={s}
            percent={percent}
            active={active}
            interactive={false}
          />
        );
      })}
    </div>
  );
};
